import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Github } from 'lucide-react';
import { useStory } from '../../context/StoryContext';

/**
 * ChapterCard — Product as narrative chapter with cross-link
 * Label (CHAPTER N · THEME), product name (brand color), tagline, narrative role, cross-link
 * Hover: border glows brand color
 *
 * Importers: src/pages/HomePage.jsx (Ecosystem section), src/pages/ContactPage.jsx (Chapters panel), src/pages/AboutPage.jsx (Products Portfolio)
 * Affected API: Default export ChapterCard component
 * Data schema: Props accept chapter (number), theme (string), name (string), color ('ice'|'goingmerry'|'ramsey'|'cortex'), tagline (string), role (string), href (string), external (boolean), index (number), className (string)
 * User instruction: "see this website is good... but the brand needs some kinda storytelling UI/UX... right? dont u think ? use ur marketing skills and make up a plan"
 */
const ChapterCard = ({
  chapter,
  theme,
  name,
  color = 'ice',
  tagline,
  role,
  href,
  external = false,
  index = 0,
  className = '',
  style,
  ...props
}) => {
  const { reducedMotion } = useStory();

  const colorMap = {
    ice: { primary: 'var(--memory-blue)', glow: 'var(--memory-blue-glow)', subtle: 'var(--memory-blue-subtle)' },
    goingmerry: { primary: 'var(--velocity-cyan)', glow: 'var(--velocity-cyan-glow)', subtle: 'var(--velocity-cyan-subtle)' },
    ramsey: { primary: 'var(--truth-amber)', glow: 'var(--truth-amber-glow)', subtle: 'var(--truth-amber-subtle)' },
    cortex: { primary: 'var(--orchestration-violet)', glow: 'var(--orchestration-violet-glow)', subtle: 'var(--orchestration-violet-subtle)' },
  };

  const colors = colorMap[color] || colorMap.ice;

  const transition = reducedMotion
    ? { duration: 0 }
    : { type: 'spring', damping: 20, stiffness: 180, delay: index * 0.1 };

  const linkProps = external
    ? { target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <motion.article
      className={`chapter-card ${className}`}
      style={{
        background: 'var(--glass-2)',
        backdropFilter: 'var(--blur-card)',
        border: '1px solid var(--glass-2-border)',
        borderRadius: '1.5rem',
        padding: '1.5rem',
        transition: 'border-color 200ms var(--ease-out), box-shadow 200ms var(--ease-out), transform 200ms var(--ease-out)',
        position: 'relative',
        overflow: 'hidden',
        ...style,
      }}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={transition}
      whileHover={{
        borderColor: colors.primary,
        boxShadow: `0 20px 40px -20px ${colors.glow}, 0 0 0 1px ${colors.primary}`,
        transform: 'translateY(-4px)',
        transition: { duration: 0.2 },
      }}
      {...props}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Chapter Badge */}
        <motion.span
          className="chapter-badge"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.375rem 1rem',
            background: colors.subtle,
            border: '1px solid',
            borderColor: colors.primary,
            borderRadius: '9999px',
            fontSize: '0.625rem',
            fontWeight: 700,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: colors.primary,
            width: 'fit-content',
            marginBottom: '1rem',
          }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: index * 0.1 + 0.1, type: 'spring', damping: 20, stiffness: 180 }}
        >
          <span>CHAPTER {chapter}</span>
          <span style={{ opacity: 0.5 }}>·</span>
          <span>{theme.toUpperCase()}</span>
        </motion.span>

        {/* Product Name */}
        <motion.h3
          className="chapter-name"
          style={{
            fontSize: '1.75rem',
            fontWeight: 700,
            color: colors.primary,
            margin: '0 0 0.5rem',
            lineHeight: 1.2,
            transition: 'color 200ms var(--ease-out)',
          }}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 + 0.15 }}
        >
          {name}
        </motion.h3>

        {/* Tagline */}
        <motion.p
          className="chapter-tagline"
          style={{
            fontSize: '0.9375rem',
            color: 'var(--text-secondary)',
            margin: '0 0 1rem',
            lineHeight: 1.6,
            fontWeight: 300,
          }}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 + 0.2 }}
        >
          {tagline}
        </motion.p>

        {/* Narrative Role */}
        <motion.p
          className="chapter-role"
          style={{
            fontSize: '0.8125rem',
            color: 'var(--text-muted)',
            margin: '0 0 1.5rem',
            lineHeight: 1.5,
            fontStyle: 'italic',
            flexGrow: 1,
          }}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: index * 0.1 + 0.25 }}
        >
          {role}
        </motion.p>

        {/* Cross-link */}
        <motion.a
          className="chapter-link"
          href={href}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            color: colors.primary,
            fontSize: '0.8125rem',
            fontWeight: 600,
            fontFamily: 'inherit',
            textDecoration: 'none',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            transition: 'gap 200ms var(--ease-out), color 200ms var(--ease-out)',
            width: 'fit-content',
          }}
          {...linkProps}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: index * 0.1 + 0.3 }}
          whileHover={{ gap: '1rem', color: 'var(--text-primary)' }}
          whileTap={{ scale: 0.98 }}
        >
          <span>Explore Chapter</span>
          <motion.span
            style={{ display: 'flex' }}
            whileHover={{ x: 4 }}
            transition={{ type: 'spring', stiffness: 400, damping: 17 }}
          >
            {external ? <Github className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
          </motion.span>
        </motion.a>

        {/* Accent bar */}
        <motion.div
          className="chapter-accent"
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: `linear-gradient(90deg, ${colors.primary}, ${colors.primary}80)`,
            transform: 'scaleX(0)',
            transformOrigin: 'left',
            transition: 'transform 400ms var(--ease-out)',
          }}
          animate={{ scaleX: 1 }}
          transition={{ delay: index * 0.1 + 0.4 }}
        />
      </div>
    </motion.article>
  );
};

export default ChapterCard;